/**
 * ApiKeysSettingsPage — User-level BYOK (Bring Your Own Key) settings page.
 *
 * AC-1514 (epic AC-1493 Phase 7A).
 *
 * Renders one `ProviderKeyCard` per supported LLM provider (OpenAI,
 * Anthropic, Google) regardless of configuration state, so the user can
 * always see which providers are supported and which are coming soon.
 *
 * The page is mounted at `/settings/api-keys` (registered in `App.tsx`)
 * and linked from the sidebar via the existing Settings entry (the
 * top-level Settings page redirects users here for the API Keys tab).
 *
 * ## Data flow
 *
 *   - Reads the user's per-provider key list via `useApiKeys()` (returns
 *     one row per provider with a `has_key` flag plus non-secret metadata).
 *   - Add / Replace / Remove / Test go through the mutation hooks in
 *     `useApiKeyMutations.ts`. Every mutation invalidates `apiKeys()` and
 *     `llmCatalog()` so the agent picker (AC-1516) and orchestration
 *     banner (AC-1517) react automatically.
 *   - The page owns the modal-open state for Add/Replace (one modal,
 *     repurposed) and renders a simple inline confirmation for Remove
 *     (no third "ConfirmModal" — Remove is destructive but reversible by
 *     re-adding the key, so we keep the UX inline and lightweight).
 *
 * ## Why providers are hard-coded
 *
 * Phase 7A ships with exactly three providers in the catalog: OpenAI,
 * Anthropic (live), and Google (placeholder). The backend's
 * `GET /api/v1/credentials` returns a row for each. The display order
 * here mirrors the backend's list ordering for stability; the labels
 * are user-facing and live on the client.
 */

import React, { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Key } from 'lucide-react';

import { LLMProvider } from '../../types';
import { useApiKeys } from '../../hooks/queries/useApiKeys';
import { useSystemMode } from '../../hooks/queries/useSystemMode';
import {
  useDeleteApiKey,
  useTestApiKey,
} from '../../hooks/queries/useApiKeyMutations';
import ProviderKeyCard from './ProviderKeyCard';
import AddApiKeyModal from './AddApiKeyModal';

interface ProviderDescriptor {
  provider: LLMProvider;
  label: string;
  comingSoon?: boolean;
}

const PROVIDERS: ProviderDescriptor[] = [
  { provider: 'openai', label: 'OpenAI' },
  { provider: 'anthropic', label: 'Anthropic' },
  { provider: 'google', label: 'Google', comingSoon: true },
];

const ApiKeysSettingsPage: React.FC = () => {
  const { data: keys, isLoading, isError, error } = useApiKeys();
  // AC-1619: read the deployment runtime mode from the authoritative
  // system-mode endpoint. Default to BYOK (true) until it resolves so we never
  // briefly hide the management CTAs for a BYOK deployment.
  const { data: systemMode } = useSystemMode();
  const byokMode = systemMode?.byok_mode ?? true;
  const deleteMutation = useDeleteApiKey();
  const testMutation = useTestApiKey();

  // Modal state. We use one modal repurposed for both add and replace.
  const [modal, setModal] = useState<
    | { open: true; provider: LLMProvider; providerLabel: string; mode: 'add' | 'replace'; initialDisplayLabel: string | null }
    | { open: false }
  >({ open: false });

  // Per-provider Test-button pending state (so other rows aren't dimmed).
  const [testingProvider, setTestingProvider] = useState<LLMProvider | null>(null);

  const keyByProvider = useMemo(() => {
    const map = new Map<LLMProvider, ReturnType<typeof Map.prototype.get>>();
    for (const k of keys ?? []) {
      map.set(k.provider, k);
    }
    return map;
  }, [keys]);

  const openAdd = (descriptor: ProviderDescriptor) => {
    setModal({
      open: true,
      provider: descriptor.provider,
      providerLabel: descriptor.label,
      mode: 'add',
      initialDisplayLabel: null,
    });
  };

  const openReplace = (descriptor: ProviderDescriptor) => {
    const existing = keyByProvider.get(descriptor.provider) as
      | { display_label?: string | null }
      | undefined;
    setModal({
      open: true,
      provider: descriptor.provider,
      providerLabel: descriptor.label,
      mode: 'replace',
      initialDisplayLabel: existing?.display_label ?? null,
    });
  };

  const handleRemove = async (descriptor: ProviderDescriptor) => {
    // Confirm before deleting — destructive operation. We keep this as a
    // window.confirm for now to match other settings flows; a richer
    // confirmation can be added later if UX needs it.
    const confirmed =
      typeof window !== 'undefined' &&
      window.confirm(
        `Remove the ${descriptor.label} API key? Agents using ${descriptor.label} models will fail to run until a key is re-added.`,
      );
    if (!confirmed) return;
    try {
      await deleteMutation.mutateAsync({ provider: descriptor.provider });
      toast.success(`${descriptor.label} API key removed.`);
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      toast.error(detail || `Failed to remove ${descriptor.label} API key.`);
    }
  };

  const handleTest = async (descriptor: ProviderDescriptor) => {
    setTestingProvider(descriptor.provider);
    try {
      const result = await testMutation.mutateAsync({
        provider: descriptor.provider,
      });
      if (result.ok) {
        toast.success(`${descriptor.label} key validated successfully.`);
      } else {
        toast.error(
          `${descriptor.label} key failed: ${result.error || 'unknown error'}`,
        );
      }
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      toast.error(detail || `Failed to test ${descriptor.label} key.`);
    } finally {
      setTestingProvider(null);
    }
  };

  return (
    <div className="p-4 sm:p-6 max-w-4xl mx-auto" data-testid="api-keys-settings-page">
      <div className="mb-6">
        <div className="flex items-center mb-2">
          <Key className="w-6 h-6 text-primary-600 mr-2" aria-hidden="true" />
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-warm-white">
            API Keys
          </h1>
        </div>
        <p className="text-gray-600 dark:text-gray-400">
          Connect your own LLM provider credentials so agents in your projects
          can use them. Keys are validated on save, stored encrypted, and never
          shared between users.
        </p>
      </div>

      {!byokMode && (
        <div
          data-testid="api-keys-platform-managed-banner"
          role="status"
          className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg mb-4"
        >
          <p className="text-sm text-blue-800 dark:text-blue-200">
            This deployment uses platform-managed API keys set in the
            environment; per-user keys are only used when BYOK mode is enabled.
          </p>
        </div>
      )}

      {isLoading && (
        <div
          data-testid="api-keys-loading"
          className="p-8 text-center text-gray-500 dark:text-gray-400 text-sm"
        >
          Loading your API keys...
        </div>
      )}

      {isError && !isLoading && (
        <div
          role="alert"
          className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg mb-4"
        >
          <p className="text-sm text-red-700 dark:text-red-300">
            Failed to load API keys: {error?.message || 'Unknown error'}
          </p>
        </div>
      )}

      {!isLoading && (
        <div className="space-y-4">
          {PROVIDERS.map((descriptor) => {
            const userKey = (keyByProvider.get(descriptor.provider) as
              | import('../../types').UserApiKey
              | undefined) ?? null;
            return (
              <ProviderKeyCard
                key={descriptor.provider}
                provider={descriptor.provider}
                providerLabel={descriptor.label}
                userKey={userKey}
                comingSoon={descriptor.comingSoon}
                byokMode={byokMode}
                isTestPending={testingProvider === descriptor.provider}
                onAdd={() => openAdd(descriptor)}
                onReplace={() => openReplace(descriptor)}
                onTest={() => handleTest(descriptor)}
                onRemove={() => handleRemove(descriptor)}
              />
            );
          })}
        </div>
      )}

      {modal.open && (
        <AddApiKeyModal
          open={modal.open}
          provider={modal.provider}
          providerLabel={modal.providerLabel}
          mode={modal.mode}
          initialDisplayLabel={modal.initialDisplayLabel}
          onClose={() => setModal({ open: false })}
          onSuccess={() => {
            toast.success(
              modal.mode === 'add'
                ? `${modal.providerLabel} API key added.`
                : `${modal.providerLabel} API key replaced.`,
            );
          }}
        />
      )}
    </div>
  );
};

export default ApiKeysSettingsPage;
